import React, { useMemo, useState } from 'react';

import { Button, Flex, Grid, Group, Input, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';

import {
  CreateAppointmentResponse,
  useCreateAppointmentMutation
} from '@/store/slices/api/schedules/schedulesApiSlice';
import { getTomorrow } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import DatePicker from '@/components/DatePickers/DatePicker';

import styles from '../DeviceScheduleModal.module.scss';
import SchedulesSummary from '../SchedulesSummary';
import { EditScheduleStepProps } from '../types';

interface FormData {
  startDate: Date | null;
  endDate: Date | null;
  frequency: string;
}

const initialFormData = { startDate: null, endDate: null, frequency: '' };

const PhysicianRequestedSchedule: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  onSuccess,
  loading,
  setLoading,
  patientSFId,
  deviceInfo
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [apiResponse, setApiResponse] = useState<CreateAppointmentResponse | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const [createAppointment] = useCreateAppointmentMutation();

  const minEndDate = useMemo(() => {
    if (!formData.startDate) return getTomorrow();
    const d = new Date(formData.startDate);
    d.setDate(d.getDate() + 1);
    return d;
  }, [formData.startDate]);

  const isEndDateValid =
    !formData.startDate || !formData.endDate ? true : formData.endDate > formData.startDate;

  const handleFormChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => {
      // We will reset end date if the start date is more than the end date
      if (
        field === 'startDate' &&
        value instanceof Date &&
        prev.endDate instanceof Date &&
        value >= prev.endDate
      ) {
        return { ...prev, startDate: value, endDate: null };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSave = async () => {
    if (
      !formData.startDate ||
      !formData.endDate ||
      !formData.frequency ||
      !patientSFId ||
      !deviceInfo
    ) {
      console.log('Missing required data for Physician Requested appointment');
      return;
    }

    setLoading(true);

    try {
      const formattedStartDate = formatDate(formData.startDate, 'YYYY-MM-DD');
      const formattedEndDate = formatDate(formData.endDate, 'YYYY-MM-DD');

      const appointmentData = {
        deviceId: deviceInfo.deviceId,
        patientSFId: patientSFId,
        scheduleDate: formattedStartDate,
        endDate: formattedEndDate,
        frequency: formData.frequency,
        type: 'Physician Request' as const,
        modelNumber: deviceInfo.modelNumber,
        serialNumber: deviceInfo.serialNumber
      };

      const result = await createAppointment(appointmentData).unwrap();

      if (!result.success) {
        console.error('API returned success: false', result);
        notifications.show({
          title: 'Error',
          message: result.message || 'Failed to create Physician Requested appointment',
          color: 'red',
          icon: <IconX size="1.1rem" />,
          autoClose: 5000
        });
        return;
      }

      notifications.show({
        title: 'Success',
        message: result.message || 'Physician Requested appointment created successfully',
        color: 'green',
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });

      setApiResponse(result);
      setShowSummary(true);
    } catch (error) {
      console.error('Error creating Physician Requested appointment:', error);

      if (error && typeof error === 'object' && 'data' in error) {
        const errorData = error.data as CreateAppointmentResponse;
        if (errorData && !errorData.success) {
          notifications.show({
            title: 'Error',
            message: errorData.message || 'Failed to create Physician Requested appointment',
            color: 'red',
            icon: <IconX size="1.1rem" />,
            autoClose: 5000
          });
          return;
        }
      }

      notifications.show({
        title: 'Error',
        message: 'Failed to create Physician Requested appointment',
        color: 'red',
        icon: <IconX size="1.1rem" />,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setShowSummary(false);
    setApiResponse(null);
    setFormData(initialFormData);

    if (apiResponse?.success) {
      onSuccess?.();
    }

    onClose();
  };

  if (showSummary && apiResponse?.summary?.nextDates) {
    return (
      <SchedulesSummary
        appointmentType="Physician Request"
        nextDates={apiResponse.summary.nextDates}
        onClose={resetAndClose}
      />
    );
  }

  const saveDisabled =
    !formData.startDate ||
    !formData.endDate ||
    !formData.frequency ||
    !isEndDateValid ||
    loading ||
    !patientSFId ||
    !deviceInfo;

  return (
    <Stack gap="xs">
      <Flex px={20} pt={16} direction="column">
        <Title order={5} fw={600} mb={4} c="plum.9">
          Physician Requested Appointment
        </Title>
        <Text size="xs" c="plum" mb={8}>
          You must set start date, end date and frequency. If any existing remote appointments
          conflict with account or patient level buffers, the dates will shift to comply with the
          Physician Requested appointments.
        </Text>
        <Grid columns={12} mt={8}>
          <Grid.Col span={4}>
            <Text size="xs" c="plum" mb={2} fw={500}>
              Start Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: formData.startDate,
                onChange: (date: Date | null) => handleFormChange('startDate', date),
                isClearable: false,
                minDate: getTomorrow(),
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" c="plum" mb={2} fw={500}>
              End Date
            </Text>
            <DatePicker
              inputProps={{
                placeholder: 'MM/DD/YYYY',
                required: true,
                disabled: loading
              }}
              datePickerProps={{
                selected: formData.endDate,
                onChange: (date: Date | null) => handleFormChange('endDate', date),
                isClearable: false,
                minDate: minEndDate,
                dateFormat: 'MM/dd/yyyy',
                disabled: loading
              }}
            />
            {formData.endDate && !isEndDateValid && (
              <Text size="xs" c="red" mt={4}>
                End date must be after start date
              </Text>
            )}
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" c="plum" mb={2} fw={500}>
              Frequency (Days)
            </Text>
            <Input
              placeholder="Enter frequency in days"
              type="number"
              min="1"
              value={formData.frequency}
              onChange={(e) => handleFormChange('frequency', e.target.value)}
              disabled={loading}
            />
          </Grid.Col>
        </Grid>
      </Flex>
      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          className={styles.modalButton}
          disabled={loading}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleSave}
          className={styles.modalButton}
          disabled={saveDisabled}
          loading={loading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default PhysicianRequestedSchedule;
